import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, User, Image as ImageIcon, Video, Loader2, Star, Trash2, CheckCircle, X, Shield, RefreshCw } from "lucide-react";
import { SwitchToggle } from "@/components/toggles";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const POSE_HINTS = [
  "Frontal · aufrecht",
  "Frontal · leicht gedreht",
  "Seitlich links",
  "Seitlich rechts",
  "Halbportrait sitzend",
  "Kniend",
  "Rückansicht",
  "Detail Oberkörper",
  "Detail Beine",
  "Freies Studio-Portrait",
];

const DEFAULT_STAGING_PRIVACY = {
  pixelate_face: false,
  blur_face: false,
  remove_tattoos: true,
};

export default function ModulGSubjectsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrollmentActive, setEnrollmentActive] = useState(false);
  const [subject, setSubject] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 3-Stufen-Staging-Flow-State
  const [staging, setStaging] = useState(null);      // {staging_id, preview_url, status, ...}
  const [stagingPreview, setStagingPreview] = useState(null); // Blob-URL für aktuelle Vorschau
  const [stagingPrivacy, setStagingPrivacy] = useState(DEFAULT_STAGING_PRIVACY);
  const [anonymizing, setAnonymizing] = useState(false);
  const [anonElapsed, setAnonElapsed] = useState(0);
  const anonAbortRef = useRef(false);
  const [confirming, setConfirming] = useState(false);

  // Video-Upload (Legacy-Flow — Video hat keine Anonymisierung in V1)
  const [pendingVideo, setPendingVideo] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);

  const imgInput = useRef(null);
  const imgCameraInput = useRef(null);
  const vidInput = useRef(null);
  const vidCameraInput = useRef(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const e = await api.get("/modul-g/enrollment");
      const active = e.data.enrollment?.status === "ACTIVE";
      setEnrollmentActive(active);
      if (active) {
        const s = await api.get("/modul-g/subject");
        setSubject(s.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { navigate("/login?next=/studio/preview/subjects"); return; }
    load();
  }, [user]);

  // Signed-Preview-URL des Staging-Bilds als Blob laden (Auth-Header wird durch api-Instanz gesetzt)
  const refreshStagingPreview = async (stagingId, variant = "anonymized") => {
    try {
      const res = await api.get(`/modul-g/subject/staging/${stagingId}/preview`, {
        params: { variant, t: Date.now() },   // Cache-Buster
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setStagingPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      // Falls anonymized noch nicht existiert → raw
      if (variant === "anonymized") {
        return refreshStagingPreview(stagingId, "raw");
      }
    }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/modul-g/subject/staging", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      setStaging(res.data);
      // Direkt Rohbild als Vorschau laden
      await refreshStagingPreview(res.data.staging_id, "raw");
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d?.errors?.join("; ") || d || "Upload fehlgeschlagen");
      if (imgInput.current) imgInput.current.value = "";
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const applyAnonymization = async () => {
    if (!staging) return;
    setAnonymizing(true); setError(""); setAnonElapsed(0);
    anonAbortRef.current = false;
    const startTs = Date.now();
    const ticker = setInterval(() => setAnonElapsed(Math.floor((Date.now() - startTs) / 1000)), 1000);
    try {
      // iter173t · Asynchron: Job starten → Status pollen (kein Proxy-Timeout).
      const { data: started } = await api.post(
        `/modul-g/subject/staging/${staging.staging_id}/anonymize`, stagingPrivacy, { timeout: 60000 });
      const statusUrl = `/modul-g/subject/staging/${staging.staging_id}/anonymize-status`;
      const deadline = Date.now() + 240000;
      let result = null;
      while (Date.now() < deadline) {
        if (anonAbortRef.current) return;
        await new Promise((r) => setTimeout(r, 2500));
        if (anonAbortRef.current) return;
        let st;
        try { st = (await api.get(statusUrl, { timeout: 30000 })).data; } catch { continue; }
        if (st.state === "DONE") { result = st; break; }
        if (st.state === "FAILED") { throw new Error(st.error || "failed"); }
      }
      if (anonAbortRef.current) return;
      if (!result) throw new Error("timeout");
      setStaging((cur) => ({ ...cur, ...result, status: "ANONYMIZED" }));
      await refreshStagingPreview(staging.staging_id, "anonymized");
    } catch (e) {
      const d = e.response?.data?.detail;
      setError((typeof d === "string" ? d : d?.message) || t("anonymize.err_timeout"));
    } finally {
      clearInterval(ticker);
      setAnonymizing(false);
    }
  };

  const confirmStaging = async () => {
    if (!staging) return;
    setConfirming(true);
    setError("");
    try {
      const nextIdx = (subject?.pose_count || 0);
      const pose_label = POSE_HINTS[nextIdx] || `Pose ${nextIdx + 1}`;
      await api.post(
        `/modul-g/subject/staging/${staging.staging_id}/confirm`,
        { pose_label },
      );
      // Staging aufräumen
      if (stagingPreview) URL.revokeObjectURL(stagingPreview);
      setStagingPreview(null);
      setStaging(null);
      setStagingPrivacy(DEFAULT_STAGING_PRIVACY);
      if (imgInput.current) imgInput.current.value = "";
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d || "Bestätigung fehlgeschlagen");
    } finally {
      setConfirming(false);
    }
  };

  const cancelStaging = async () => {
    anonAbortRef.current = true;
    setAnonymizing(false);
    if (!staging) return;
    try {
      await api.delete(`/modul-g/subject/staging/${staging.staging_id}`);
    } catch (e) { /* ignore */ }
    if (stagingPreview) URL.revokeObjectURL(stagingPreview);
    setStagingPreview(null);
    setStaging(null);
    setStagingPrivacy(DEFAULT_STAGING_PRIVACY);
    if (imgInput.current) imgInput.current.value = "";
  };

  // Video-Legacy-Flow
  const uploadVideo = async (file) => {
    setPendingVideo(file);
    setVideoUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("asset_type", "BASE_VIDEO");
      const dur = await new Promise((resolve) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => resolve(v.duration);
        v.onerror = () => resolve(null);
        v.src = URL.createObjectURL(file);
      });
      if (dur) fd.append("duration_seconds", dur.toFixed(1));
      await api.post("/modul-g/subject/assets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d?.errors?.join("; ") || d || "Video-Upload fehlgeschlagen");
    } finally {
      setPendingVideo(null);
      setVideoUploading(false);
      if (vidInput.current) vidInput.current.value = "";
    }
  };

  const makePrimary = async (assetId) => {
    try { await api.post(`/modul-g/assets/${assetId}/primary`); await load(); } catch (e) { setError(t("modul_g_subjects.error")); }
  };

  const deleteAsset = async (assetId) => {
    if (!window.confirm(t("modul_g_subjects.confirm_delete_pose"))) return;
    try { await api.delete(`/modul-g/assets/${assetId}`); await load(); } catch (e) { setError(t("modul_g_subjects.error")); }
  };

  if (loading) return <div data-testid="mg-subject-loading" className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#666]" /></div>;

  if (!enrollmentActive) {
    return (
      <div data-testid="mg-subject-gated" className="min-h-screen bg-white p-6 flex flex-col items-center justify-center text-center">
        <p className="text-sm mb-3">{t("modul_g_subjects.not_active")}</p>
        <button data-testid="mg-subject-goto-enrollment" onClick={() => navigate("/studio/preview")} className="h-10 px-4 rounded-full bg-[#0A0A0A] text-white text-sm">Zum Enrollment</button>
      </div>
    );
  }

  const poses = subject?.poses || [];
  const poseCount = poses.length;
  const minSuggested = subject?.readiness?.min_suggested || 5;
  const maxAllowed = subject?.readiness?.max_allowed || 10;
  const canAddMore = poseCount < maxAllowed;
  const readyForPreview = subject?.readiness?.ready_for_preview;
  const readyFull = subject?.readiness?.ready_full;

  return (
    <div data-testid="mg-subject-page" className="min-h-screen bg-white pb-24">
      <div className="sticky top-0 z-40 flex items-center gap-3 h-14 px-4 border-b border-[#E5E5E5] bg-white">
        <button onClick={() => navigate("/studio/preview")} className="text-[#0A0A0A]" data-testid="mg-subject-back"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[#999]">Modul G · Mein Motiv</p>
          <h1 className="text-base font-bold">{subject?.label || "Mein Motiv"}</h1>
        </div>
      </div>

      {/* Readiness-Kachel */}
      <div className={`mx-4 mt-4 border p-4 ${readyFull ? "border-emerald-500 bg-emerald-50" : readyForPreview ? "border-amber-400 bg-amber-50" : "border-[#E5E5E5]"}`}>
        <div className="flex items-center gap-2 mb-1">
          {readyFull ? <CheckCircle size={16} className="text-emerald-600" /> : <User size={16} className="text-[#666]" />}
          <p className="text-sm font-semibold" data-testid="mg-subject-readiness-label">
            {readyFull ? `Bereit für Preview · ${poseCount}/${maxAllowed} Posen` : readyForPreview ? `Test-Preview möglich · noch ${minSuggested - poseCount} Pose(n) empfohlen` : `Noch keine Pose · Empfohlen: ${minSuggested}-${maxAllowed}`}
          </p>
        </div>
        <p className="text-xs text-[#666]">
          Lade Ganzkörper-Portrait-Fotos hoch (Portrait-Ausrichtung, min. 800 × 1000 px). Wird proportional auf max. 2048 px lange Kante skaliert — kein Beschnitt.
        </p>
      </div>

      {/* Info: 3-Stufen-Anonymisierung */}
      <div className="mx-4 mt-3 border-l-2 border-emerald-500 bg-emerald-50 p-3 flex gap-2">
        <Shield size={14} className="text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-[11px] text-emerald-900">
          <strong>3-Stufen-Anonymisierung:</strong> Upload → Anonymisieren (Gesicht + optional Tattoos) → Bestätigen.
          Sobald du bestätigst, wird das Original gelöscht — nur das anonymisierte Bild bleibt gespeichert.
        </p>
      </div>

      {error && (
        <div data-testid="mg-subject-error" className="mx-4 mt-3 border border-red-400 bg-red-50 text-red-700 text-xs p-3">{error}</div>
      )}

      {/* Upload-Buttons — nur sichtbar wenn kein Staging aktiv */}
      {!staging && (
        <>
          <div className="mx-4 mt-4 grid grid-cols-2 gap-2">
            <button
              data-testid="mg-subject-upload-image-btn"
              onClick={() => imgInput.current?.click()}
              disabled={uploading || !canAddMore}
              className="h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40">
              <Plus size={14} /> Pose (Galerie)
            </button>
            <button
              data-testid="mg-subject-upload-image-camera-btn"
              onClick={() => imgCameraInput.current?.click()}
              disabled={uploading || !canAddMore}
              className="h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40">
              📷 Pose (Kamera)
            </button>
            <button
              data-testid="mg-subject-upload-video-btn"
              onClick={() => vidInput.current?.click()}
              disabled={videoUploading || subject?.has_base_video}
              className="h-11 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40">
              <Video size={14} /> {subject?.has_base_video ? "Video vorhanden" : "Video (Galerie)"}
            </button>
            <button
              data-testid="mg-subject-upload-video-camera-btn"
              onClick={() => vidCameraInput.current?.click()}
              disabled={videoUploading || subject?.has_base_video}
              className="h-11 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40">
              🎥 Video (Kamera)
            </button>
          </div>
          <input ref={imgInput} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
          <input ref={imgCameraInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
          <input ref={vidInput} type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} />
          <input ref={vidCameraInput} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} />
        </>
      )}

      {uploading && (
        <div data-testid="mg-subject-upload-progress" className="mx-4 mt-2 border border-[#E5E5E5] p-2">
          <div className="h-1 bg-[#F5F5F5] overflow-hidden">
            <div className="h-full bg-[#0A0A0A]" style={{ width: `${uploadProgress}%` }} />
          </div>
          <p data-testid="mg-subject-upload-label" className="text-[10px] text-[#0A0A0A] mt-1 inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Wird hochgeladen … {uploadProgress}%</p>
        </div>
      )}

      {videoUploading && (
        <div className="mx-4 mt-2 border border-[#E5E5E5] p-3">
          <p className="text-[11px] text-[#0A0A0A] flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Wird hochgeladen …</p>
        </div>
      )}

      {/* Pose-Grid */}
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">Posen ({poseCount}/{maxAllowed})</p>
        {poseCount === 0 && !staging && (
          <div data-testid="mg-subject-empty" className="border border-dashed border-[#E5E5E5] p-8 text-center">
            <ImageIcon size={32} className="mx-auto text-[#CCC] mb-2" />
            <p className="text-sm text-[#666]">{t("modul_g_subjects.no_pose")}</p>
            <p className="text-xs text-[#999] mt-1">Klick oben auf &bdquo;Pose hinzuf&uuml;gen&ldquo;.</p>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {poses.map((p) => (
            <div key={p.asset_id} data-testid={`mg-pose-${p.pose_index}`} className={`relative border ${p.is_primary ? "border-[#0A0A0A]" : "border-[#E5E5E5]"} aspect-[3/4] overflow-hidden bg-[#F5F5F5]`}>
              <img src={p.url} alt={p.pose_label || `Pose ${p.pose_index}`} className="w-full h-full object-contain" />
              <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white p-1.5">
                <p className="text-[10px] font-mono">#{p.pose_index}</p>
                <p className="text-[11px] truncate">{p.pose_label || "Pose"}</p>
              </div>
              {p.is_primary && (
                <div data-testid={`mg-pose-primary-badge-${p.pose_index}`} className="absolute top-1.5 left-1.5 bg-[#0A0A0A] text-white text-[9px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <Star size={9} /> Titelbild
                </div>
              )}
              <div className="absolute top-1.5 right-1.5 flex gap-1">
                {!p.is_primary && (
                  <button data-testid={`mg-pose-primary-btn-${p.pose_index}`} onClick={() => makePrimary(p.asset_id)} className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center" title="Als Titelbild"><Star size={11} /></button>
                )}
                <button data-testid={`mg-pose-delete-${p.pose_index}`} onClick={() => deleteAsset(p.asset_id)} className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center" title={t("modul_g_subjects.delete")}><Trash2 size={11} className="text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Base-Video */}
        {subject?.base_video && (
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">Base-Video</p>
            <div data-testid="mg-subject-base-video" className="border border-[#E5E5E5] p-2 flex items-center gap-3">
              <Video size={20} className="text-[#666]" />
              <div className="flex-1 text-xs">
                <p className="font-mono truncate">{subject.base_video.original_filename}</p>
                <p className="text-[#999]">{subject.base_video.duration_seconds}s · {Math.round((subject.base_video.size || 0) / 1024)} KB</p>
              </div>
              <button data-testid="mg-subject-video-delete" onClick={() => deleteAsset(subject.base_video.asset_id)} className="text-red-500 text-xs underline">{t("common.delete")}</button>
            </div>
          </div>
        )}
      </div>

      {readyForPreview && !staging && (
        <div className="fixed bottom-16 left-0 right-0 px-4">
          <button data-testid="mg-subject-goto-requests" onClick={() => navigate("/studio/preview/requests")} className="w-full h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium">
            {t("modul_g_subjects.back_to_config")}
          </button>
        </div>
      )}

      {/* Staging-Dialog (Stufe 2 & 3: Anonymisierung + Bestätigung) */}
      {staging && (
        <div data-testid="mg-staging-dialog" className="fixed inset-0 z-[9999] bg-black/80 flex items-end sm:items-center justify-center overflow-y-auto">
          <div className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
            <div className="sticky top-0 bg-white flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5]">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <Shield size={14} /> {staging.status === "ANONYMIZED" ? "Vorschau prüfen & bestätigen" : "Anonymisierung wählen"}
              </p>
              <button onClick={cancelStaging} className="p-1.5 -mr-1.5" data-testid="mg-staging-close" disabled={confirming}>
                <X size={18} />
              </button>
            </div>

            {/* Preview-Bild */}
            <div className="bg-[#F5F5F5] flex items-center justify-center" style={{ minHeight: "40vh" }}>
              {stagingPreview ? (
                <img
                  src={stagingPreview}
                  data-testid="mg-staging-preview-img"
                  alt="Staging-Vorschau"
                  className="max-w-full object-contain"
                  style={{ maxHeight: "40vh" }}
                />
              ) : (
                <Loader2 className="animate-spin text-[#666]" />
              )}
              {anonymizing && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="bg-white border border-[#E5E5E5] px-4 py-3 flex flex-col items-center gap-2 w-60">
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span className="text-xs font-semibold">{t("anonymize.working")}</span>
                    </div>
                    <p className="text-[10px] text-[#666] text-center leading-snug">{t("anonymize.working_hint")}</p>
                    <p data-testid="mg-anon-elapsed" className="text-[11px] text-[#0A0A0A] font-medium">{anonElapsed}s</p>
                  </div>
                </div>
              )}
            </div>

            {/* Toggles */}
            <div className="p-4 space-y-2">
              <p className="text-[11px] text-[#666]">
                Wähle Anonymisierung und klick <strong>{t("modul_g_subjects.preview_quote")}</strong>{t("modul_g_subjects.after_quote")} <strong>&bdquo;&Uuml;bernehmen&ldquo;</strong>. Danach wird das Original gel&ouml;scht.
              </p>

              {/* Face-Effect (Mosaik+Blur kombiniert) */}
              <div className="border border-[#E5E5E5] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">Gesicht</p>
                <label data-testid="mg-staging-face-anonymize" className="flex items-center justify-between py-1 cursor-pointer">
                  <div>
                    <span className="text-sm">Gesicht anonymisieren</span>
                    <p className="text-[10px] text-[#999] mt-0.5">{t("modul_g_subjects.mask_note")}</p>
                  </div>
                  <SwitchToggle
                    checked={stagingPrivacy.pixelate_face}
                    disabled={anonymizing || confirming}
                    onChange={() => setStagingPrivacy((cur) => ({
                      ...cur,
                      pixelate_face: !cur.pixelate_face,
                      blur_face: false,
                    }))}
                    variant="neutral"
                    ariaLabel={t("modul_g_subjects.mask_note")}
                  />
                </label>
              </div>

              {/* Marks-Removal */}
              <div className="border border-[#E5E5E5] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">{t("modul_g_subjects.body_markers")}</p>
                {[
                  { key: "remove_tattoos",    label: "Tattoos entfernen", hint: t("anonymize.remove_tattoos_hint") },
                ].map((row) => {
                  const on = !!stagingPrivacy[row.key];
                  return (
                    <label key={row.key} data-testid={`mg-staging-toggle-${row.key}`} className="flex items-center justify-between py-1.5 cursor-pointer">
                      <div className="pr-3">
                        <span className="text-sm">{row.label}</span>
                        {row.hint && <p className="text-[10px] text-[#999] mt-0.5 leading-snug">{row.hint}</p>}
                      </div>
                      <SwitchToggle
                        checked={on}
                        disabled={anonymizing || confirming}
                        onChange={() => setStagingPrivacy((cur) => ({ ...cur, [row.key]: !on }))}
                        variant="neutral"
                        ariaLabel={row.label}
                      />
                    </label>
                  );
                })}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  data-testid="mg-staging-anonymize-btn"
                  onClick={applyAnonymization}
                  disabled={anonymizing || confirming}
                  className="flex-1 h-12 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
                  {anonymizing ? <><Loader2 size={13} className="animate-spin" /> Anonymisiere…</> : <><RefreshCw size={13} /> {t("modul_g_subjects.create_preview")}</>}
                </button>
                <button
                  data-testid="mg-staging-confirm-btn"
                  onClick={confirmStaging}
                  disabled={anonymizing || confirming || staging.status !== "ANONYMIZED"}
                  className="flex-1 h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
                  {confirming ? <><Loader2 size={13} className="animate-spin" /> Speichere…</> : <><CheckCircle size={13} /> {t("modul_g_subjects.apply")}</>}
                </button>
              </div>
              {staging.status !== "ANONYMIZED" && (
                <p className="text-[10px] text-[#999] text-center">{t("modul_g_subjects.click_preview_first")}</p>
              )}
              <button
                data-testid="mg-staging-cancel-btn"
                onClick={cancelStaging}
                disabled={confirming}
                className="w-full h-10 rounded-full text-[#666] text-xs disabled:opacity-40">
                Abbrechen &amp; verwerfen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
